import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingScreenService } from 'app/shared/services/loading-screen/loading-screen.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CustomerFormService } from '../../customer-form/_services/customer-form.service';
import { LanguageService } from '../../shared/services/language.service';
import { TranslationService, Translations } from '../../shared/services/translation.service';
import { TurnstileComponent } from "../../shared/turnstile.component";
import { EmailVerificationService } from '../_services/email-verification.service';

@Component({
  selector: 'app-email-verification',
  templateUrl: './email-verification.component.html',
  styleUrls: ['./email-verification.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    TranslateModule,
    MatCardModule,
    MatProgressSpinnerModule,
    TurnstileComponent
],
  providers: [TranslationService, LanguageService],
})
export class EmailVerificationComponent implements OnInit, OnDestroy, AfterViewInit {
  errorMessages: { [key: string]: string } = {};

  emailForm: FormGroup;
  // emailVerified = false; // Flag for email verification
  generatedOtp: string | null = null;
  isOTPGenerated = false;
  currentLang: 'en' | 'zh' = 'en';
  currentYear = new Date().getFullYear();
  translations!: Translations;
  otpVerified = false;
  otpError = false;
  otpSent = false;
  sendOtpError = false;
  @Output() verified = new EventEmitter<void>();
  private translationSubscription?: Subscription;
  siteKey = environment.siteKey;
  turnstileToken = '';

  constructor(
    private fb: FormBuilder,
    private languageService: LanguageService,
    private translationService: TranslationService,
    private snackBar: MatSnackBar,
    private emailVerificationService: EmailVerificationService,
    private customerFormService: CustomerFormService,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
    private http: HttpClient,
    private loadingScreenService: LoadingScreenService
  ) {
    this.emailForm = this.fb.group({
      email: [
        {
          value: '',
          disabled: false,
        },
        [
          Validators.required,
          Validators.pattern(
            "^[\\w!#$%&'+/=?`{|}~^-]+(?:\\.[\\w!#$%&'+/=?`{|}~^-]+)*@(?:[a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,6}$"
          ),
        ],
      ],
      otp: [
        {
          value: '',
          disabled: false,
        },
        Validators.required,
      ],
    });
  }

  ngOnInit() {
    this.loadingScreenService.show();
    this.loadErrorMessages();
    this.translationSubscription =
      this.translationService.translations$.subscribe((translations) => {
        this.translations = translations;
      });

    this.languageService.currentLanguage$.subscribe((lang: string) => {
      this.currentLang = lang as 'en' | 'zh';
    });
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.loadingScreenService.hide();
    });
  }

  loadErrorMessages(): void {
    this.loadingScreenService.show();
    let folder = "en_AU";
    if(localStorage.getItem('selectedLanguage') === "zh"){
      folder = "zh_CN"
    }
    this.http
      .get<{ [key: string]: string }>(
        `/assets/i18n/${folder}/Common/email-verification.json`
      )
      .pipe(finalize(() => this.loadingScreenService.hide()))
      .subscribe({
        next: (data) => {
          this.errorMessages = data;
        },
        error: (err) => {
          console.error('Failed to load error messages:', err);
        },
      });
  }

  validateEmail(): void {
    //commented for future reference
    // const emailControl = this.emailForm.get('email'); 
    // if (emailControl?.valid) {
    //   console.log('Email verified:', emailControl.value);
    // } else {
    //   console.warn('Invalid email address:', emailControl?.value);
    // }
  }

  ngOnDestroy() {
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
  }
  sendOtp() {
    this.loadingScreenService.show();
    if (!this.emailForm.get('email')?.valid) {
      this.toastr.error(
        this.translations.emailInvalid,
        'Error'
      );
      this.loadingScreenService.hide();
      return;
    }

    if (this.emailForm.get('email')?.valid) {
      const email = this.emailForm.get('email')?.value;

      this.emailVerificationService
        .generateOtp(email, this.currentLang)
        .pipe(finalize(() => this.loadingScreenService.hide()))
        .subscribe({
          next: (response) => {
            this.generatedOtp = 'sent';
            this.otpSent = true;
            this.sendOtpError = false;
            this.toastr.success(
              this.translations.otpSentSuccess,
              this.translations.success
            );

            setTimeout(() => (this.otpSent = false), 3000);
          },
          error: (error) => {
            this.handleError(error, this.translations.otpSendError);
            this.sendOtpError = true;
            this.generatedOtp = null;

            setTimeout(() => (this.sendOtpError = false), 3000);
          },
        });
    }
    this.otpNotRecieved();
  }

  verifyOtp() {
    this.loadingScreenService.show();
    const email = this.emailForm.get('email')?.value;
    const enteredOtp = this.emailForm.get('otp')?.value;

    if (email && enteredOtp) {
      this.emailVerificationService.validateOtp(email, enteredOtp).pipe(
        finalize(() => this.loadingScreenService.hide())
      ).subscribe({
        next: (response) => {
          this.otpVerified = true;
          this.otpError = false;

          if (response?.uuidToken) {
            localStorage.setItem('uuidToken', response.uuidToken);
            sessionStorage.setItem('verifiedEmail', email);

            this.toastr.success(
              this.translations.otpVerified,
              this.translations.success
            );

            this.loadingScreenService.show();
            setTimeout(() => {
              this.verified.emit();
              this.loadingScreenService.hide();
            }, 2000);
          } else {
            this.toastr.error(
              String(
                this.translations.authenticationFalied
              ),
              this.translations.error
            );
          }
        },
        error: (error) => {
          this.handleError(error, this.translations.otpVerifyError);
          this.otpError = true;
          this.otpVerified = false;
        },
      });
    } else {
      this.loadingScreenService.hide();
      this.otpError = true;
      this.toastr.error(this.translations.otpempty, 'Error');
    }
  }

  resendOtp() {
    this.loadingScreenService.show();
    const email = this.emailForm.get('email')?.value;

    if (email) {
      this.emailVerificationService
        .generateOtp(email, this.currentLang)
        .pipe(finalize(() => this.loadingScreenService.hide()))
        .subscribe({
          next: () => {
            this.otpSent = true;
            this.emailForm.patchValue({ otp: '' });

            this.toastr.success(
              this.translations.otpResendSuccess,
              this.translations.success
            );

            setTimeout(() => (this.otpSent = false), 3000);
          },
          error: (error) => {
            this.handleError(error, this.translations.otpSendError);
            this.sendOtpError = true;

            setTimeout(() => (this.sendOtpError = false), 3000);
          },
        });
    } else {
      this.loadingScreenService.hide();
    }
  }

  switchLanguage(lang: 'en' | 'zh') {
    this.currentLang = lang;
    this.languageService.setLanguage(lang);
  }

  private handleError(error: any, fallback: string) {
    const errorCode = error.error?.error || error.error?.messageCode;
    const message = this.errorMessages[errorCode] || fallback;
    this.toastr.error(message, 'Error');
  }

  preventSpace(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
  }

  otpNotRecieved(): void{
   // Start 45-second timer when flag becomes true
    setTimeout(() => {
      this.isOTPGenerated = true;
    }, 45000); // 45000 ms = 45 seconds
  }

  onTokenReceived(token: string) {
    this.turnstileToken = token;
    // console.log(this.turnstileToken);
  }  
}
